import { useState } from 'react'

import { api, ApiFehler } from '../api'
import { useAktion, useLaden } from '../hooks'
import Meldung from '../komponenten/Meldung'
import Meldungen from '../komponenten/Meldungen'
import { zeitpunkt } from '../format'
import type { Regel, RegelRueckbau, RegelVorschlag, RegelVorschlagAntwort } from '../typen'

const ARTTEXT: Record<string, string> = {
  kopplung: 'Wer zieht welches Material',
  umrechnung: 'Umrechnung in Tonnen',
  gruppe: 'Zuordnung zum Blatt',
  mindestbestand: 'Mindestbestand',
  summenspalte: 'Zusätzliche Summenspalte',
  stammdaten: 'Stammdaten ändern',
  artikel: 'Neuer Artikel',
  einstellung: 'Einstellung der Disposition',
}

const BEISPIEL =
  'Beschreib in eigenen Worten, wie die Tabelle aufgebaut sein soll. Zum Beispiel:\n' +
  'Der Deckel Würfel 10 kg wird von Sanna, Marina und Sonja gezogen.\n' +
  'Der Sanna Wickler ist ein 250-g-Wickler, also 4.000 Stück je Tonne.\n' +
  'Bei den Würfeln brauche ich eine Summenspalte über die drei Tagesproduktionen.\n' +
  'Alle Wickler von Beucke haben 13 Wochen Lieferzeit.\n' +
  'Den Neutralkarton führen wir nicht mehr.\n' +
  'Neuer Artikel 930299 OTH. Kokos Wickler 6 kg, 250-g-Wickler von Wegoflex, gezogen vom Kokosfett Würfel 6 kg.\n' +
  'Der Puffer für Bestellungen soll 6 Wochen sein.'

/**
 * Löschen oder Abschalten - und wenn eine Stelle seitdem anders geändert wurde (409), fragen, ob die
 * Regel trotzdem gehen soll. Diese Stellen bleiben dann, wie sie sind.
 */
async function mitRueckfrage<T>(aktion: (erzwingen: boolean) => Promise<T>): Promise<T | undefined> {
  try {
    return await aktion(false)
  } catch (f) {
    if (!(f instanceof ApiFehler) || f.status !== 409) throw f
    if (!window.confirm(`${f.message}\n\nTrotzdem fortfahren?`)) return undefined
    return await aktion(true)
  }
}

function rueckbauText(r: RegelRueckbau): string {
  const teile = ['Regel gelöscht.']
  teile.push(r.zurueckgesetzt.length ? `Zurückgesetzt: ${r.zurueckgesetzt.join(', ')}.` : 'An den Stammdaten war nichts zurückzusetzen.')
  if (r.uebersprungen.length) teile.push(`Unverändert gelassen: ${r.uebersprungen.join(', ')}.`)
  return teile.join(' ')
}

/**
 * Regeln beschreiben, wie die Kladde aufgebaut ist: welcher Fertigartikel welches Material zieht,
 * womit umgerechnet wird, was in welches Blatt gehört. Das steht nicht im Programm - es wird hier
 * frei beschrieben, von der KI in eine Regel übersetzt und nach Freigabe gespeichert.
 */
export default function RegelnSeite() {
  const [text, setText] = useState('')
  const [antwort, setAntwort] = useState<RegelVorschlagAntwort | null>(null)
  const [uebernommen, setUebernommen] = useState<Set<number>>(new Set())
  const { fehler, erfolg, arbeitet, ausfuehren, setErfolg } = useAktion()
  const { daten: geladeneRegeln, fehler: ladeFehler, neuLaden: laden } = useLaden<Regel[]>(() => api.regeln(), [])
  const regeln = geladeneRegeln ?? []

  function vorschlagen() {
    setAntwort(null)
    setUebernommen(new Set())
    void ausfuehren(async () => setAntwort(await api.regelVorschlag(text)))
  }

  function uebernehmen(vorschlag: RegelVorschlag, index: number) {
    void ausfuehren(async () => {
      await api.regelSpeichern(vorschlag, text)
      setUebernommen((alt) => new Set(alt).add(index))
      laden()
    }, 'Regel gespeichert. Die Kladde baut sich beim nächsten Aufruf danach auf.')
  }

  function umschalten(regel: Regel) {
    void ausfuehren(async () => {
      const ergebnis = await mitRueckfrage((erzwingen) => api.regelAktiv(regel.id, !regel.aktiv, erzwingen))
      laden()
      if (ergebnis) setErfolg(regel.aktiv ? 'Regel abgeschaltet - ihre Änderungen an den Stammdaten sind zurückgesetzt.' : 'Regel wieder angewendet.')
    })
  }

  function loeschen(regel: Regel) {
    void ausfuehren(async () => {
      const ergebnis = await mitRueckfrage((erzwingen) => api.regelLoeschen(regel.id, erzwingen))
      laden()
      if (ergebnis) setErfolg(rueckbauText(ergebnis))
    })
  }

  return (
    <>
      <h2>Aufbau der Tabelle beschreiben</h2>
      <p className="hinweis">
        Was hier steht, bestimmt, wie die Kladde aussieht und gerechnet wird. Es wird nichts
        übernommen, bevor du es freigibst.
      </p>

      <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={BEISPIEL} />

      <div className="leiste" style={{ marginTop: 10 }}>
        <button className="haupt" disabled={arbeitet || !text.trim()} onClick={vorschlagen}>
          Regel vorschlagen
        </button>
        {arbeitet && <span className="hinweis">Wird verarbeitet.</span>}
      </div>

      <Meldungen fehler={ladeFehler ?? fehler} erfolg={erfolg} />

      {antwort?.rueckfragen.length ? (
        <Meldung art="warnung" titel="Vor dem Speichern zu klären">
          <ul>
            {antwort.rueckfragen.map((frage, i) => (
              <li key={i}>{frage}</li>
            ))}
          </ul>
        </Meldung>
      ) : null}

      {antwort?.vorschlaege.map((vorschlag, index) => {
        const blockiert = vorschlag.einwaende.length > 0
        return (
          <div key={index} className="karte">
            <div className="leiste" style={{ marginBottom: 8 }}>
              <span className="status">{ARTTEXT[vorschlag.art] ?? vorschlag.art}</span>
              <b>{vorschlag.titel}</b>
              {vorschlag.sicherheit === 'unsicher' && (
                <span className="status gesperrt">unsicher</span>
              )}
            </div>

            {vorschlag.begruendung && <p className="hinweis">{vorschlag.begruendung}</p>}

            {blockiert ? (
              <Meldung art="fehler" titel="Diese Regel lässt sich nicht übernehmen">
                <ul>
                  {vorschlag.einwaende.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </Meldung>
            ) : (
              <table style={{ width: 'auto', minWidth: 0, marginBottom: 10 }}>
                <thead>
                  <tr>
                    <th className="h1">Betrifft</th>
                    <th className="h1">Was</th>
                    <th className="h1">bisher</th>
                    <th className="h1">künftig</th>
                  </tr>
                </thead>
                <tbody>
                  {vorschlag.vorschau.map((zeile, i) => (
                    <tr key={i}>
                      <td>{zeile.gegenstand}</td>
                      <td>{zeile.feld}</td>
                      <td>{zeile.alt}</td>
                      <td>
                        <b>{zeile.neu}</b>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <button
              className="haupt"
              disabled={arbeitet || blockiert || uebernommen.has(index)}
              onClick={() => uebernehmen(vorschlag, index)}
            >
              {uebernommen.has(index) ? 'Übernommen' : 'Übernehmen'}
            </button>
          </div>
        )
      })}

      <h2 style={{ marginTop: 28 }}>Gespeicherte Regeln</h2>
      <div className="tabellenrahmen">
        <table>
          <thead>
            <tr>
              <th>Art</th>
              <th>Regel</th>
              <th>Wortlaut</th>
              <th>Angelegt</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {regeln.map((regel) => (
              <tr key={regel.id}>
                <td>{ARTTEXT[regel.art] ?? regel.art}</td>
                <td style={{ whiteSpace: 'normal', maxWidth: 380 }}>{regel.titel}</td>
                <td style={{ whiteSpace: 'normal', maxWidth: 320 }} className="hinweis">
                  {regel.freitext ?? ''}
                </td>
                <td>{zeitpunkt(regel.angelegt_am)}</td>
                <td>
                  <span className={`status ${regel.aktiv ? 'gebucht' : ''}`}>
                    {regel.aktiv ? 'aktiv' : 'abgeschaltet'}
                  </span>
                </td>
                <td>
                  <button disabled={arbeitet} onClick={() => umschalten(regel)}>
                    {regel.aktiv ? 'Abschalten' : 'Einschalten'}
                  </button>{' '}
                  <button disabled={arbeitet} onClick={() => loeschen(regel)}>
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {regeln.length === 0 && <p className="leer">Noch keine Regeln gespeichert.</p>}
      </div>
    </>
  )
}
